(() => {
  // One completed result, owned by this page/selection, never a paid retry cache.
  const storageFailure = error => Boolean(error?.storageFailure
    || /^(?:IMAGE_WRITE_FAILED(?:_TIMEOUT)?|IMAGE_WRITE_VERIFICATION_FAILED|IMAGE_READ_FAILED(?:_TIMEOUT)?|INDEXEDDB_(?:UNAVAILABLE|TIMEOUT)|DRAFT_HEADER_WRITE_FAILED)$/.test(error?.code || error?.message || ''));
  const message = 'Your image was created, but this browser could not save it. Try saving again or download the image below. Neither option generates another image. Keep this tab open until you have saved it. If saving keeps failing, download it before opening normal browsing and upload that file there.';
  const create = ({ root, isCurrent, save, onState, onError, onExpired }) => {
    const panel = root.querySelector('[data-generated-recovery]');
    const notice = root.querySelector('[data-generated-recovery-message]');
    const retry = root.querySelector('[data-generated-recovery-save]');
    const download = root.querySelector('[data-generated-recovery-download]');
    const details = root.querySelector('[data-generated-recovery-diagnostics]');
    let pending = null, objectUrl = '', timer = 0, saving = false;
    const clear = () => {
      pending = null;
      window.clearTimeout(timer); timer = 0;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      objectUrl = '';
      if (download) download.removeAttribute('href');
      if (panel) panel.hidden = true;
      if (details) details.value = '';
      onState(false);
    };
    const current = () => {
      if (pending && (pending.record.expiresAt <= Date.now() || !isCurrent(pending))) clear();
      return pending;
    };
    const diagnose = error => {
      if (!details || !pending) return;
      const diagnostic = error?.storageFailure || {};
      const kinds = ['quota', 'unavailable', 'aborted', 'timeout', 'unknown'];
      const names = ['QuotaExceededError', 'AbortError', 'UnknownError', 'InvalidStateError', 'NotAllowedError', 'SecurityError', 'DataCloneError', 'TransactionInactiveError', 'Error', 'ConstraintError', 'DataError', 'InvalidAccessError', 'NotFoundError', 'NotReadableError', 'NotSupportedError', 'ReadOnlyError', 'VersionError'];
      // No prompt, artwork, URL, record identifier or raw browser error message.
      details.value = JSON.stringify({ format: 'dim-image-storage-diagnostics@1', stage: 'save-generated-image',
        kind: kinds.includes(diagnostic.kind) ? diagnostic.kind : 'unknown',
        name: names.includes(diagnostic.name) ? diagnostic.name : 'UnknownError',
        operation: ['open', 'open-blocked', 'upgrade', 'transaction', 'request', 'existing', 'put'].includes(diagnostic.stage) ? diagnostic.stage : 'unknown',
        nativeCode: Number.isInteger(diagnostic.code) && diagnostic.code >= 0 && diagnostic.code <= 65535 ? diagnostic.code : null,
        width: pending.record.width, height: pending.record.height, bytes: pending.record.blob.size,
        mimeType: pending.record.mimeType, recordedAt: Date.now() }, null, 2);
    };
    const offer = (record, snapshot, error) => {
      clear();
      pending = { record, snapshot };
      if (!current()) return false;
      if (download) {
        objectUrl = URL.createObjectURL(record.blob);
        download.href = objectUrl;
        download.download = record.originalName || 'dream-it-made-image.png';
      }
      if (panel) panel.hidden = false;
      if (notice) notice.textContent = message;
      if (retry) retry.disabled = false;
      diagnose(error);
      onState(true);
      timer = window.setTimeout(() => { if (pending?.record === record) { clear(); onExpired(); } },
        Math.max(0, Math.min(record.expiresAt - Date.now(), 7200000)));
      return true;
    };
    const retrySave = async () => {
      const owner = current();
      if (!owner || saving) return;
      saving = true;
      if (retry) { retry.disabled = true; retry.setAttribute('aria-busy', 'true'); }
      try {
        await save(owner.record, owner.snapshot, () => pending === owner && Boolean(current()));
        if (pending === owner) clear();
      } catch (error) {
        if (pending === owner && current()) { diagnose(error); onError(error, message); }
      } finally {
        saving = false;
        if (retry) { retry.disabled = false; retry.removeAttribute('aria-busy'); }
      }
    };
    const guardDownload = event => { if (!current()) event.preventDefault(); };
    retry?.addEventListener('click', retrySave);
    download?.addEventListener('click', guardDownload);
    return { offer, clear, hasPending: () => Boolean(current()), retry: retrySave,
      focus: () => retry?.focus(),
      destroy: () => { clear(); retry?.removeEventListener('click', retrySave); download?.removeEventListener('click', guardDownload); } };
  };
  window.DIMGeneratedRecovery = Object.freeze({ create, storageFailure, message });
})();
